// Shared workspace sync — NO AUTH, test mode.
// Hydrates localStorage from R2 on boot and pushes changes back via a
// debounced PUT (1.5s after the last write). Single shared JSON blob,
// last-writer-wins semantics. App.js is untouched: we monkey-patch
// localStorage.setItem so any code that writes one of SHARED_KEYS triggers
// a save automatically.

const API = process.env.REACT_APP_API_URL || 'https://timbo-production.up.railway.app';

// Validated with Yann (2026-06-05). Concepts first, per brief priority.
// Excludes: legacy keys, credentials, blob-URL state, per-browser UI state,
// branding, creators/HOV (not in brief scope), custom_voices (audit pending).
export const SHARED_KEYS = [
  // Concepts (priority)
  'timbo_feature_concepts_v2',
  // Scripts / Feature Manager configs (primary source)
  'timbo_feature_configs_v2',
  // Hooks (client-side list — server-side meta lives in hooks_meta.json on R2)
  'timbo_hooks',
  // Feature Manager — custom features (App.js + FeatureManager.js touch both keys)
  'timbo_custom_features',
  'timbo_custom_features_v1',
  // Winning entries (Feature-Manager-adjacent)
  'timbo_winning_v1',
  // Performance verdict thresholds (v4.02 — shared so "winner" means the same thing for everyone)
  'timbo_perf_rules',
  'timbo_perf_rules_v2',
  // Export platform tags (TikTok, Shopify…): list, labels, shown/hidden
  'timbo_export_platform_tags_v1',
];

export async function loadWorkspace() {
  try {
    // Cap the wait so a slow/down backend can never block first paint for long.
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort(), 4000);
    let res;
    try { res = await fetch(`${API}/api/workspace`, { cache: 'no-store', signal: ctrl.signal }); }
    finally { clearTimeout(to); }
    if (!res.ok) return;
    const data = await res.json();
    for (const key of SHARED_KEYS) {
      if (typeof data[key] === 'string') localStorage.setItem(key, data[key]);
    }
  } catch (e) {
    console.warn('[workspace] load failed', e);
  }
}

let timer = null;
function scheduleSave() {
  clearTimeout(timer);
  timer = setTimeout(async () => {
    const payload = {};
    for (const key of SHARED_KEYS) {
      const raw = localStorage.getItem(key);
      if (raw !== null) payload[key] = raw;
    }
    try {
      const res = await fetch(`${API}/api/workspace`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        console.warn('[workspace] save rejected', res.status, b.error || '');
      }
    } catch (e) {
      console.warn('[workspace] save failed', e);
    }
  }, 1500);
}

export function initWorkspaceSync() {
  const orig = localStorage.setItem.bind(localStorage);
  localStorage.setItem = (key, value) => {
    orig(key, value);
    if (SHARED_KEYS.includes(key)) scheduleSave();
  };
}
